import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient, ApiError, type UserVariable } from '../lib/api';

type FormState = {
  name: string;
  type: 'secret' | 'text';
  value: string;
  description: string;
};

const emptyForm: FormState = { name: '', type: 'secret', value: '', description: '' };

export function Variables() {
  const [vars, setVars] = useState<UserVariable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setVars(await apiClient.variables.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[A-Z][A-Z0-9_]*$/.test(form.name)) {
      setError('Name must be UPPER_SNAKE_CASE.');
      return;
    }
    if (!form.value) {
      setError('Value is required.');
      return;
    }
    setBusy(true);
    try {
      await apiClient.variables.create({
        name: form.name,
        type: form.type,
        value: form.value,
        description: form.description || undefined,
      });
      setForm(emptyForm);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete ${name}? This can't be undone.`)) return;
    try {
      await apiClient.variables.delete(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed');
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      <Link to="/library" className="text-[12px] text-text-muted hover:text-[var(--color-fg)]">
        ← Library
      </Link>
      <h1 className="mt-3 text-lg font-semibold">Variables</h1>
      <p className="mt-1 text-[13px] text-text-subtle">
        Values referenced by your skills as <code className="font-mono">${'{NAME}'}</code>{' '}
        placeholders. Secrets stay on the server; the CLI fetches them at sync time and never
        commits them to GitHub.
      </p>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          {vars?.length ?? 0} variable{vars?.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className={adding ? 'btn' : 'btn btn-primary'}
        >
          {adding ? 'Cancel' : '+ Add variable'}
        </button>
      </div>

      {adding && (
        <form onSubmit={handleSubmit} className="mt-3 rounded-lg border bg-surface p-4">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <label className="block text-[10px] uppercase tracking-wider text-text-subtle">
              Name (UPPER_SNAKE_CASE)
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))
                }
                placeholder="ANTHROPIC_API_KEY"
                className="mt-1 block w-full rounded border bg-canvas px-2 py-1.5 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
                autoFocus
              />
            </label>
            <label className="block text-[10px] uppercase tracking-wider text-text-subtle">
              Type
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as 'secret' | 'text' }))}
                className="mt-1 block w-full rounded border bg-canvas px-2 py-1.5 text-[12px] outline-none focus:border-[var(--color-accent)]"
              >
                <option value="secret">secret</option>
                <option value="text">text</option>
              </select>
            </label>
          </div>
          <label className="mt-3 block text-[10px] uppercase tracking-wider text-text-subtle">
            Value
            <input
              type={form.type === 'secret' ? 'password' : 'text'}
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              placeholder={form.type === 'secret' ? 'sk-…' : 'plain text'}
              className="mt-1 block w-full rounded border bg-canvas px-2 py-1.5 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
            />
          </label>
          <label className="mt-3 block text-[10px] uppercase tracking-wider text-text-subtle">
            Description (optional)
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="mt-1 block w-full rounded border bg-canvas px-2 py-1.5 text-[12px] outline-none focus:border-[var(--color-accent)]"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <button type="submit" disabled={busy} className="btn btn-primary">
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <ul className="mt-5 divide-y rounded-lg border bg-canvas">
        {vars === null && <li className="px-4 py-3 text-text-subtle">Loading…</li>}
        {vars?.length === 0 && (
          <li className="px-4 py-6 text-center text-text-subtle">
            No variables yet — add one when a skill needs to be installed.
          </li>
        )}
        {vars?.map((v) => (
          <li key={v.id} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-2.5">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13px]">${'{'}{v.name}{'}'}</span>
                <span
                  className={
                    v.type === 'secret'
                      ? 'chip chip-warning'
                      : 'chip'
                  }
                >
                  {v.type}
                </span>
              </div>
              <p className="mt-0.5 font-mono text-[11px] text-text-subtle">{v.value}</p>
              {v.description && (
                <p className="mt-0.5 text-[11px] text-text-subtle">{v.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleDelete(v.id, v.name)}
              className="btn text-[11px]"
              title="Delete variable"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
